<script setup>
import { i18n } from 'boot/i18n';
import {
  HrFormDialog,
  HrConfirmBtn,
  HrDismissBtn,
  HrListSelect,
  HrTextField,
} from 'src/design-system';
import { useCountries } from 'src/composables/countries';
import { computed, ref } from 'vue';

import CalendarDateField from './CalendarDateField.vue';

/**
 * Record somebody who holds a client's policy, or correct somebody already on file.
 *
 * ONE FORM FOR BOTH, because the fields are identical and the only difference is whether an id
 * exists. Two forms would drift the moment either gained a field.
 *
 * DELIBERATELY NOT THE PICKER'S JOB, and deliberately not a general person form. The picker is a
 * capability other parts of the product will reuse; what a policyholder needs is specific to
 * billing, and it is a much shorter list than a full person record. Keeping them apart is what lets
 * the picker move without dragging this with it.
 *
 * NOTHING HERE IS REQUIRED EXCEPT A NAME, and that is the point rather than an oversight. A card
 * arrives with a name and a member id, sometimes a date of birth, almost never a gender or an address.
 * Blocking the save would produce placeholder birthdays, which are worse than blanks because nothing
 * downstream can tell them apart from real ones.
 *
 * NO STANDING NOTICE ABOUT WHAT IS MISSING. This form carried one and it was wrong twice over: it
 * said the same thing whether the form was empty or complete, and it listed the fields a payer wants
 * from a policyholder who is ALSO the patient, which is not the case this form is usually opened for.
 * What is actually outstanding depends on the coverage, so the coverage says it, once, accurately.
 *
 * A DIALOG RATHER THAN A BOTTOM SHEET, unlike the picker it is reached from. A sheet is the right
 * mobile shape for short focused content, which a search field over a list is; eight fields is not.
 * `HrFormDialog` goes full screen on mobile of its own accord and scrolls its body, which is what a
 * form this size needs on a phone.
 *
 * @interfaceShape imperative
 */
defineOptions({ name: 'AddPolicyholderDialog' });

const props = defineProps({
  /**
   * The clinic's country, which shapes the address fields.
   *
   * Supplied rather than looked up: a person inherits their clinic's country on the server, so the
   * form has to ask the same question of the same answer, and reaching into app state from inside
   * the context to get it would be the dependency pointing the wrong way.
   */
  country: {
    type: String,
    default: null,
  },
});

const emit = defineEmits({
  /** The save failed. The form stays open on the values it had, so it can be tried again. */
  error: null,
  hide: null,
});

const dialog = ref(null);
const isSaving = ref(false);

/**
 * What to do with the collected profile, handed over when the form is opened.
 *
 * A FUNCTION RATHER THAN AN EVENT, because the form has to know when the work finished and an event
 * cannot tell it: `emit` returns nothing, so awaiting one waits for nothing at all. A form that
 * closed on its own after emitting closed while the write was still in flight, which showed no
 * saving state worth the name and left the record looking unchanged if you reopened it straight
 * away. Holding the caller's function is what makes the close, the spinner and the failure honest.
 */
let save = null;

const blank = () => ({
  firstName: '',
  lastName: '',
  birthDate: null,
  gender: null,
  address1: '',
  city: '',
  state: '',
  postalCode: '',
});

const draft = ref(blank());

/**
 * What was typed is a name, but which parts of one is a guess, so this splits at the LAST space:
 * everything before it is the given name and the final word is the family name.
 *
 * One word goes to the given name, not the family name. Somebody typing a single word in a search
 * has typed what they call the person, and landing it in Last name reads as a mistake the form made.
 *
 * Both fields stay editable, so a wrong split costs a correction rather than a bad record. Which is
 * the point of guessing at all: retyping a name that is already on screen is the worse option.
 */
function splitTypedName(typed) {
  const parts = typed.trim().split(/\s+/).filter(Boolean);

  return {
    firstName: parts.slice(0, -1).join(' ') || parts[0] || '',
    lastName: parts.length > 1 ? parts[parts.length - 1] : '',
  };
} // splitTypedName()

const isEditing = ref(false);

/**
 * A person record holds the display name and the name parts separately, and older ones have only the
 * display name. Splitting it is the same guess `splitTypedName` already makes for a search, it is
 * just as editable, and it beats opening blank on a person whose name is on screen — which reads as
 * the form having lost it, and leaves somebody retyping what they can see.
 *
 * Only when BOTH parts are absent. One part filled is a record that has been answered, and a display
 * name carrying more than it does is not grounds to overwrite the answer.
 */
const namePartsOf = (person) => {
  if (person.firstName ?? person.lastName) {
    return { firstName: person.firstName ?? '', lastName: person.lastName ?? '' };
  }

  return splitTypedName(person.fullName ?? '');
};

/**
 * The demographics projection speaks payer terms; the form writes storage terms. One field differs
 * in name between them, and mapping it here rather than renaming either is what keeps the projection
 * free to describe an address the way a claim does.
 */
const fromPerson = (person) => ({
  ...namePartsOf(person),
  birthDate: person.dateOfBirth,
  gender: person.gender,
  address1: person.addressLine1 ?? '',
  city: person.city ?? '',
  state: person.state,
  postalCode: person.postalCode ?? '',
});

const genderOptions = ['female', 'male', 'other']
  .map((value) => ({ label: i18n.t(`addPolicyholder.gender.${value}`), value }));

const {
  countryHasStates,
  countryStates,
  countryStateLabel,
  countryHasPostalCode,
  countryPostalCodeLabel,
} = useCountries();

/**
 * The address follows the country rather than assuming one shape.
 *
 * A subdivision is a CLOSED LIST, not free text: a payer matches on the code, so "Oregon", "Ore."
 * and "OR" are three ways to fail the same claim. Countries that have no subdivision do not show the
 * field at all, and the one that has no postal code does not show that either. An empty field
 * somebody cannot fill is a question they will assume they got wrong.
 */
const hasStates = computed(() => countryHasStates(props.country));
const stateLabel = computed(() => countryStateLabel(props.country));
const stateOptions = computed(() => {
  return countryStates(props.country).map(({ code, label }) => ({ label, value: code }));
});

const hasPostalCode = computed(() => countryHasPostalCode(props.country));
const postalCodeLabel = computed(() => countryPostalCodeLabel(props.country));

const nameRules = [
  () => Boolean(draft.value.firstName.trim() || draft.value.lastName.trim())
    || i18n.t('addPolicyholder.inputNameError'),
];

/**
 * WHAT THE FORM OPENS ON IS AN ARGUMENT, NOT A PROP, and that is load-bearing rather than a matter
 * of taste. A caller sets its own state and calls `show()` in the same tick, but a prop does not
 * reach this component until the CALLER re-renders, a tick later. The dialog therefore opened, and
 * its fields mounted, against the previous value: blank. Everything then corrected itself once the
 * prop landed, which is why it looked right under test and wrong in a browser, where the fields had
 * already taken their initial value by then. Carrying the input into the call fills the draft
 * synchronously, before anything mounts, so there is no window to be wrong in.
 *
 * @param {Object} [options]
 * @param {import('../../domain/model/PolicyholderDemographics').PolicyholderDemographics|null}
 *   [options.person] - somebody already on file, when correcting rather than recording
 * @param {string} [options.typedName] - what was typed in the picker, so nothing is retyped
 * @param {(profile: Object) => Promise<void>} [options.save] - what to do with the result. Awaited:
 *   the form closes when it resolves and stays open when it rejects.
 */
function show({ person = null, typedName = '', save: onSave = null } = {}) {
  save = onSave;
  isEditing.value = person !== null;
  draft.value = person === null
    ? { ...blank(), ...splitTypedName(typedName) }
    : { ...blank(), ...fromPerson(person) };

  dialog.value?.show();
} // show()

function hide() {
  dialog.value?.hide();
} // hide()

async function onSubmit() {
  // The dialog now stays open while the save runs, and this form submits on Enter in any field, so
  // without this a second Enter starts a second save. Recording a person is not idempotent, so that
  // is a duplicate of exactly the kind the picker is arranged to prevent.
  if (isSaving.value) {
    return;
  }

  if (!draft.value.firstName.trim() && !draft.value.lastName.trim()) {
    return;
  }

  isSaving.value = true;
  try {
    await save?.({ ...draft.value });
    hide();
  } catch (error) {
    // Left open, on the values it had. Closing on a failure would look exactly like succeeding, and
    // the reader would find out by reopening the record and seeing nothing they typed.
    emit('error', error);
  } finally {
    isSaving.value = false;
  }
} // onSubmit()

defineExpose({ show, hide });
</script>

<template>
  <hr-form-dialog
    ref="dialog"
    size="sm"
    :title="i18n.t(isEditing ? 'addPolicyholder.editTitle' : 'addPolicyholder.title')"
    data-e2e="dialog-add-policyholder"
    @submit="onSubmit"
    @hide="emit('hide')"
  >
    <div class="row q-col-gutter-md">
      <hr-text-field
        v-model="draft.firstName"
        class="col-12 col-sm-6"
        :label="i18n.t('addPolicyholder.fields.firstName')"
        :rules="nameRules"
        hide-bottom-space
        data-e2e="input-policyholder-first-name"
      />

      <hr-text-field
        v-model="draft.lastName"
        class="col-12 col-sm-6"
        :label="i18n.t('addPolicyholder.fields.lastName')"
        :rules="nameRules"
        hide-bottom-space
        data-e2e="input-policyholder-last-name"
      />

      <calendar-date-field
        v-model="draft.birthDate"
        class="col-12 col-sm-6"
        :label="i18n.t('addPolicyholder.fields.dateOfBirth')"
        clearable
        data-e2e="input-policyholder-date-of-birth"
      />

      <hr-list-select
        v-model="draft.gender"
        class="col-12 col-sm-6"
        :label="i18n.t('addPolicyholder.fields.gender')"
        :options="genderOptions"
        clearable
        hide-bottom-space
        data-e2e="select-policyholder-gender"
      />

      <hr-text-field
        v-model="draft.address1"
        class="col-12"
        :label="i18n.t('addPolicyholder.fields.addressLine1')"
        hide-bottom-space
      />

      <!-- Widths follow what each field actually holds. A subdivision is a long word plus a clear
           button and a dropdown arrow, so it needs more room than a postal code, which is five to
           ten characters and never truncates. -->
      <hr-text-field
        v-model="draft.city"
        :class="hasStates ? 'col-12 col-sm-5' : 'col-12 col-sm-8'"
        :label="i18n.t('addPolicyholder.fields.city')"
        hide-bottom-space
      />

      <hr-list-select
        v-if="hasStates"
        v-model="draft.state"
        class="col-7 col-sm-4"
        :label="stateLabel"
        :options="stateOptions"
        clearable
        hide-bottom-space
        data-e2e="select-policyholder-state"
      />

      <hr-text-field
        v-if="hasPostalCode"
        v-model="draft.postalCode"
        :class="hasStates ? 'col-5 col-sm-3' : 'col-12 col-sm-4'"
        :label="postalCodeLabel"
        hide-bottom-space
        data-e2e="input-policyholder-postal-code"
      />
    </div>

    <template #actions>
      <hr-dismiss-btn
        :label="i18n.t('common.buttonCancel')"
        @click="hide"
      />
      <hr-confirm-btn
        :label="i18n.t(isEditing ? 'addPolicyholder.actionUpdate' : 'addPolicyholder.actionSave')"
        :loading="isSaving"
        type="submit"
        data-e2e="button-save-policyholder"
      />
    </template>
  </hr-form-dialog>
</template>
